/* Common Imports */

import React from "react";

/* Redux & Type Imports */

import { ObjectId } from "bson";
import { NextRouter } from "next/router";
import StateInterface from "../../../../../redux-magic/state-interface";
import {
	SetPropertyBoostDataStateThunk,
	SetPropertyBoostMobileStepStateThunk,
	SetPropertyBoostModalStateThunk,
} from "../../../../../redux-magic/store";

/* Component Imports */

import { Skeleton, useTheme } from "@mui/material";
import BeegruButton from "../../../../common-components/buttons/BeegruButton";
import CreateNewBroadcastModal from "../../../../page-sections/Broadcasts/CreateNewBroadcastModal";

/* Icon Imports */

import { HourglassTopOutlined, OfflineBoltOutlined, TaskAltOutlined, CampaignOutlined } from "@mui/icons-material";

const PropertyCardCTA = ({
	loading,
	router,
	published_status,
	dispatch,
	slug,
	title,
	created_by,
	isEditor,
	isBoosted,
	profile_context,
	broadcastData,
	transactionType,
	propertyId,
	business_profile_id,
	session,
	propertyStatus,
}: {
	loading: boolean;
	router: NextRouter;
	published_status: string | undefined;
	dispatch: Function;
	slug: string;
	title: string;
	created_by: any;
	isEditor: boolean | undefined;
	isBoosted: boolean;
	profile_context: StateInterface["profile_context"];
	broadcastData: StateInterface["broadcast_data"] | null | undefined;
	transactionType: string;
	propertyId: ObjectId | string;
	business_profile_id: string | ObjectId | undefined | null;
	session: StateInterface["redux_session"];
	propertyStatus: string;
}) => {
	const theme = useTheme();
	const [open, setOpen] = React.useState(false);
	/* Translations */

	const localLang = {
		active_boost:
			router.locale === "ar-AE" ? "التعزيز النشط" : router.locale === "kn-IN" ? "ಸಕ್ರಿಯ ಬೂಸ್ಟ್" : "Active Boost",
		boost_now: router.locale === "ar-AE" ? "تعزيز الآن" : router.locale === "kn-IN" ? "ಈಗ ಬೂಸ್ಟ್ ಮಾಡಿ" : "Boost now",
		in_review: router.locale === "ar-AE" ? "تحت المراجعة" : router.locale === "kn-IN" ? "ಪುನರಾವಲೋಕನದಲ್ಲಿ" : "In review",
		publish_now: router.locale === "ar-AE" ? "نشر الآن" : router.locale === "kn-IN" ? "ಈಗ ಪ್ರಕಟಿಸಿ" : "Publish now",
		published: router.locale === "ar-AE" ? "نشر" : router.locale === "kn-IN" ? "ಪ್ರಕಟಿಸಲಾಗಿದೆ" : "Published",
		broadcast_now:
			router.locale === "ar-AE" ? "البث الآن" : router.locale === "kn-IN" ? "ಈಗ ಪ್ರಸಾರ ಮಾಡಿ" : "Broadcast now",
	};

	/* Let's check if the user is allowed to boost */

	const canUserBoost: boolean = profile_context
		? profile_context.user_id === (created_by ? (created_by.user_id ? created_by.user_id._id : "") : "")
		: false;

	if (loading) {
		return (
			<Skeleton
				animation="wave"
				variant="text"
				width="75%"
				height="auto"
				style={{ marginBottom: "0.5rem" }}
			/>
		);
	} else {
		return isEditor ? (
			published_status === "published" && canUserBoost ? (
				isBoosted ? (
					/* Active Boost */

					<BeegruButton
						flavor="boost"
						variant="contained"
						sx={{
							width: "100%",
							borderRadius: "0rem 0rem 0.75rem 0.75rem",
							fontSize: "0.875rem",
							"&.MuiButton-contained": {
								background: "#e7dfe8",
								color: "#693A70",
								boxShadow: "none",
								"&:focus": {
									background: "#e7dfe8",
									color: "#693A70",
									boxShadow: "none",
								},
								"&:hover": {
									background: "#e7dfe8",
									color: "#693A70",
									boxShadow: "none",
								},
							},
							/*910*/
							[theme.breakpoints.up("sm910")]: {
								fontSize: "0.95rem",
							},
						}}
						onClick={(event: any) => {
							event.preventDefault();

							dispatch(
								SetPropertyBoostModalStateThunk({
									state: true,
									id: slug,
									title: title,
									createdBy: created_by,
									active: isBoosted,
								}),
							);

							/* Fetch Boost Data */

							dispatch(SetPropertyBoostDataStateThunk({ slug: slug }));

							/* Showing summary page & cancel button if boost is active */

							dispatch(SetPropertyBoostMobileStepStateThunk({ step: 2 }));
						}}
					>
						<OfflineBoltOutlined
							sx={{
								fontSize: "1rem",
								margin: "0rem 0.25rem 0rem 0rem",
							}}
						/>
						{localLang.active_boost}
					</BeegruButton>
				) : (
					/* Broadcast now */
					<>
						<BeegruButton
							flavor="broadcast"
							variant="contained"
							sx={{
								width: "100%",
								borderRadius: "0rem 0rem 0.75rem 0.75rem",
								fontSize: "0.875rem",
								// background: "#621B16",
								"&.MuiButton-contained": {
									boxShadow: "none",
								},
								/*910*/
								[theme.breakpoints.up("sm910")]: {
									fontSize: "0.95rem",
								},
							}}
							onClick={(event: any) => {
								event.preventDefault();
								setOpen(true);
								// dispatch(
								// 	SetPropertyBoostModalStateThunk({
								// 		state: true,
								// 		id: slug,
								// 		title: title,
								// 		createdBy: created_by,
								// 		active: isBoosted,
								// 	}),
								// );

								// /* Fetch Boost Data */

								// dispatch(SetPropertyBoostDataStateThunk({ slug: slug }));

								// /* Showing first page since boost is inactive */

								// dispatch(SetPropertyBoostMobileStepStateThunk({ step: 0 }));
							}}
						>
							<CampaignOutlined
								sx={{
									fontSize: "1.25rem",
									margin: "0rem 0.5rem 0rem 0rem",
								}}
							/>
							{localLang.broadcast_now}
						</BeegruButton>

						<CreateNewBroadcastModal
							router={router}
							setOpen={setOpen}
							open={open}
							propertyStatus={propertyStatus}
							session={session}
							profile_context={profile_context}
							business_profile_id={business_profile_id}
							dispatch={dispatch}
							broadcast={broadcastData}
							transactionType={transactionType}
							propertyId={propertyId}
						/>
					</>
				)
			) : published_status === "publish_requested" || published_status === "pending_qc" ? (
				/* In review */

				<BeegruButton
					flavor="primary"
					variant="contained"
					sx={{
						width: "100%",
						borderRadius: "0rem 0rem 0.75rem 0.75rem",
						fontSize: "0.875rem",
						"&.MuiButton-contained": {
							"&.Mui-disabled": {
								"&.Mui-disabled": {
									background: "#feeada",
									color: "#B05911",
									boxShadow: "none",
								},
							},
						},
						/*910*/
						[theme.breakpoints.up("sm910")]: {
							fontSize: "0.95rem",
						},
					}}
					disabled
				>
					<HourglassTopOutlined
						sx={{
							fontSize: "1rem",
							margin: "0rem 0.25rem 0rem 0rem",
						}}
					/>
					{localLang.in_review}
				</BeegruButton>
			) : published_status === "published" && !canUserBoost ? (
				/* Published */

				<BeegruButton
					flavor="primary"
					variant="contained"
					sx={{
						width: "100%",
						borderRadius: "0rem 0rem 0.75rem 0.75rem",
						fontSize: "0.875rem",
						"&.MuiButton-contained": {
							"&.Mui-disabled": {
								background: "#feeada",
								color: "#B05911",
								boxShadow: "none",
							},
						},
						/*910*/
						[theme.breakpoints.up("sm910")]: {
							fontSize: "0.95rem",
						},
					}}
					disabled
				>
					<TaskAltOutlined
						sx={{
							fontSize: "1.25rem",
							margin: "0rem 0.25rem 0rem 0rem",
						}}
					/>
					{localLang.published}
				</BeegruButton>
			) : (
				/* Publish now */

				<BeegruButton
					flavor="primary"
					variant="contained"
					sx={{
						width: "100%",
						borderRadius: "0rem 0rem 0.75rem 0.75rem",
						fontSize: "0.875rem",
						"&.MuiButton-contained": {
							boxShadow: "none",
						},
						/*910*/
						[theme.breakpoints.up("sm910")]: {
							fontSize: "0.95rem",
						},
					}}
				>
					{localLang.publish_now}
				</BeegruButton>
			)
		) : null;
	}
};

export default PropertyCardCTA;
